import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";

const STORAGE_KEY = "corpusjuris-theme";

/**
 * Reads the initial theme once, BEFORE React mounts, so we avoid a FOUC.
 * Default is "dark" (midnight navy). Honours user's saved preference.
 */
export function getInitialTheme() {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
  } catch (e) { /* ignore */ }
  return "dark";
}

export function applyTheme(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

export default function ThemeToggle({ variant = "nav", scrolled = false }) {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch (e) { /* ignore */ }
  }, [theme]);

  const toggle = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
  const isDark = theme === "dark";

  // Desktop/mobile nav pill
  if (variant === "nav") {
    // Over-hero (transparent navbar) styles vs scrolled styles
    const baseStyle = scrolled
      ? "border-[#d9cdb4] hover:border-[#b89968] text-[#3b4a6b] hover:text-[#0f1d3a] bg-white/70"
      : "border-white/20 hover:border-[#b89968] text-[#e0d5c0] hover:text-white bg-white/5";
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
        data-testid="theme-toggle"
        className={`flex items-center justify-center w-9 h-9 rounded-full border transition-all hover:shadow-md hover:shadow-[#b89968]/20 backdrop-blur-sm ${baseStyle}`}
      >
        {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>
    );
  }

  // Mobile — full-width row
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="theme-toggle-mobile"
      className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-[#3b4a6b] hover:bg-[#ece4d6] hover:text-[#0f1d3a] rounded-lg"
    >
      <span className="flex items-center gap-3">
        {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        {isDark ? "Light Theme" : "Dark Glossy Theme"}
      </span>
      <span className="text-xs text-[#8892ad] tracking-wider uppercase">
        {isDark ? "On" : "Off"}
      </span>
    </button>
  );
}
